import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import { getStaffs } from 'reducers/staffs/StaffsSelectors';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    DateTimePicker,
    DateTimePickerItem
} from 'components/OnlineBooking/DateTimePicker/DateTimePicker';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { getOnlineBookingEvaluationItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingEvaluationItem, OnlineBookingStep } from 'types/OnlineBooking';

export const OnlineBookingEvaluationDatePicker: React.FC = () => {
    const marketplace = useMarketplace();
    const { isPending } = useOnlineBookingState();

    const staffs = useSelector(getStaffs);
    const evaluationBookingItems = useSelector(
        getOnlineBookingEvaluationItems
    ) as OnlineBookingEvaluationItem[];

    const isValid = React.useMemo(() => {
        return evaluationBookingItems.every(
            item => item.date !== undefined && item.time !== undefined
        );
    }, [evaluationBookingItems]);

    const backHandler = React.useCallback(() => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    }, []);

    const dateTimePickerChangeHandler = React.useCallback(
        (updatedItems: Array<DateTimePickerItem>) => {
            // Convert back to evaluation booking items format
            const updatedEvaluationItems = evaluationBookingItems.map((originalItem, index) => {
                const updatedItem = updatedItems[index];
                if (!updatedItem) return originalItem;

                let staff = originalItem.staff;
                if (!staff && updatedItem.time?.staffId) {
                    staff = staffs.find(s => s.id === updatedItem.time?.staffId);
                }

                return {
                    ...originalItem,
                    date: updatedItem.date,
                    time: updatedItem.time,
                    staff
                };
            });

            dispatch(updateOnlineBookingBookingItems(updatedEvaluationItems));
        },
        [evaluationBookingItems, staffs]
    );

    const submitHandler = React.useCallback(() => {
        // After evaluation date selection, go back to service category to continue with other services
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, []);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full mb-5">Select date & time</h1>

                <DateTimePicker
                    items={evaluationBookingItems.map(item => ({
                        pet: item.pet,
                        service: item.service,
                        date: item.date,
                        time: item.time
                    }))}
                    enableNextAvailableDate
                    onChange={dateTimePickerChangeHandler}
                />

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        disabled={isPending || !isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
